import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DollarSign, FileText, Users, Send, RefreshCw, Briefcase, Plus, ArrowRight, Inbox, Wallet } from 'lucide-react';
import { NotificationPrefsCard } from '@/components/NotificationPrefsCard';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const fmt$ = v => { if (!v) return '$0'; if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`; if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`; return `$${Math.round(v).toLocaleString()}`; };

export default function BrokerDashboardPage() {
  const api = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [brokerage, setBrokerage] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/broker/dashboard')
      .then(r => setData(r.data))
      .catch(err => setError(err?.response?.data?.detail || 'Failed to load dashboard'));
    api.get('/broker/action-inbox').then(r => setInbox(r.data)).catch(() => setInbox({ count: 0, items: [] }));
    api.get('/broker/brokerage').then(r => setBrokerage(r.data)).catch(() => setBrokerage(null));
  }, [api]);

  if (error) return <div className="p-6 text-sm" style={{ color: C.red }} data-testid="broker-dashboard-error">{error}</div>;
  if (!data) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}</div>;

  const k = data.kpis;
  return (
    <div className="space-y-5" data-testid="broker-dashboard">
      {user?.affinity_partner_name && (
        <div className="px-3 py-2 rounded-md text-xs" data-testid="affinity-portal-banner"
          style={{ background: 'rgba(108,75,181,0.07)', border: '1px solid rgba(108,75,181,0.25)', color: C.navy }}>
          <b style={{ color: '#6C4BB5' }}>Affinity partner portal — {user.affinity_partner_name}.</b>{' '}
          Business submitted here is labelled affinity channel; your agreement commission is applied deterministically at bind.
        </div>
      )}
      {brokerage && !brokerage.completeness.complete && (
        <button onClick={() => navigate('/brokerage')} className="w-full text-left px-3 py-2 rounded-md text-xs flex items-center justify-between gap-3" data-testid="brokerage-incomplete-banner"
          style={{ background: 'rgba(232,160,32,0.08)', border: '1px solid rgba(232,160,32,0.35)', color: C.navy }}>
          <span><b style={{ color: C.amber }}>Complete the {brokerage.name} profile.</b> Still missing: {brokerage.completeness.missing.join(', ')} — these appear on your schedules and invoices.</span>
          <ArrowRight className="w-3.5 h-3.5 shrink-0" style={{ color: C.amber }} />
        </button>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: C.navy }}>Broker Dashboard</h2>
          <p className="text-xs mt-0.5" style={{ color: C.slate }}>Your book at a glance — {data.broker_name}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate('/new-business?tab=submit')} data-testid="broker-quick-quote-btn"
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-semibold text-white transition-opacity hover:opacity-90"
            style={{ background: '#FF9933' }}>
            <Plus className="w-3.5 h-3.5" />New Quote
          </button>
          <button onClick={() => navigate('/clients')} data-testid="broker-view-clients-btn"
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-semibold transition-colors hover:bg-gray-50"
            style={{ border: `1px solid rgba(26,76,124,0.3)`, color: C.blue }}>
            <Users className="w-3.5 h-3.5" />Clients
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <KPI icon={DollarSign} label="Book GWP" value={fmt$(k.gwp)} color={C.navy} testid="broker-kpi-gwp" />
        <KPI icon={Wallet} label="Commission YTD" value={fmt$(k.commission_ytd)} sub="est. @ 15%" color={C.green} testid="broker-kpi-commission" />
        <KPI icon={FileText} label="Active Policies" value={k.active_policies} color={C.blue} testid="broker-kpi-policies" />
        <KPI icon={Users} label="Clients" value={k.clients} color={C.teal} testid="broker-kpi-clients" />
        <KPI icon={Send} label="Pending Quotes" value={k.pending_quotes} color={k.pending_quotes > 0 ? C.amber : C.slate} testid="broker-kpi-pending" />
        <KPI icon={RefreshCw} label="Renewals Due 60d" value={k.renewals_due_60d} sub={fmt$(k.renewals_due_premium)} color={k.renewals_due_60d > 0 ? C.blue : C.slate} testid="broker-kpi-renewals" />
        <KPI icon={Briefcase} label="Open Claims" value={k.open_claims} color={k.open_claims > 0 ? C.red : C.green} testid="broker-kpi-claims" />
      </div>

      {/* Action Inbox */}
      {inbox && inbox.count > 0 && (
        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(232,160,32,0.35)', background: 'rgba(232,160,32,0.03)' }} data-testid="broker-action-inbox">
          <div className="flex items-center gap-2">
            <Inbox className="w-4 h-4" style={{ color: '#B27516' }} />
            <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#B27516' }}>Needs your action ({inbox.count})</h3>
          </div>
          <div className="mt-2 space-y-0">
            {inbox.items.map((it, i) => (
              <button key={i} onClick={() => navigate(it.link)} data-testid={`inbox-item-${it.type}-${i}`}
                className="w-full flex items-center gap-3 py-2 border-b text-left text-xs hover:bg-white transition-colors rounded-sm px-1"
                style={{ borderColor: 'rgba(136,150,168,0.08)' }}>
                <span className="w-2 h-2 rounded-full shrink-0"
                  style={{ background: it.severity === 'high' ? C.red : it.severity === 'medium' ? C.amber : C.slate }} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate" style={{ color: C.navy }}>{it.title}</p>
                  {it.detail && <p className="text-[10px] truncate" style={{ color: C.slate }}>{it.detail}</p>}
                </div>
                <ArrowRight className="w-3.5 h-3.5 shrink-0" style={{ color: C.blue }} />
              </button>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="broker-upcoming-renewals">
          <SectionHead title="Upcoming Renewals (60 days)" action="All renewals" onAction={() => navigate('/renewals')} />
          {data.upcoming_renewals.length === 0 ? <Empty msg="No renewals due in the next 60 days." /> : (
            <div className="space-y-0 mt-2">
              {data.upcoming_renewals.map(r => (
                <div key={r.renewal_id} className="flex items-center gap-3 py-2 border-b text-xs" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate" style={{ color: C.navy }}>{r.business_name}</p>
                    <p className="text-[10px]" style={{ color: C.slate }}>{r.insurance_class} · {r.renewal_reference}</p>
                  </div>
                  <span className="font-mono shrink-0" style={{ color: C.navy }}>{fmt$(r.renewal_premium)}</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0"
                    style={{ background: (r.days_to_expiry ?? 99) <= 14 ? 'rgba(226,75,74,0.1)' : 'rgba(232,160,32,0.12)', color: (r.days_to_expiry ?? 99) <= 14 ? C.red : '#B27516' }}>
                    {r.days_to_expiry != null ? `${r.days_to_expiry}d` : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="broker-pending-quotes">
          <SectionHead title="Pending Quotes" action="Pipeline" onAction={() => navigate('/new-business?tab=pipeline')} />
          {data.pending_quotes.length === 0 ? <Empty msg="No quotes awaiting a decision." /> : (
            <div className="space-y-0 mt-2">
              {data.pending_quotes.map(s => (
                <div key={s.submission_id} className="flex items-center gap-3 py-2 border-b text-xs" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate" style={{ color: C.navy }}>{s.business_name}</p>
                    <p className="text-[10px]" style={{ color: C.slate }}>{s.insurance_class || s.class || 'NB'} · {s.created_at ? new Date(s.created_at).toLocaleDateString('en-AU') : ''}</p>
                  </div>
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-medium capitalize shrink-0"
                    style={{ background: 'rgba(232,160,32,0.12)', color: '#B27516' }}>{(s.status || '').replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="broker-commission-trend">
        <SectionHead title="Commission — last 6 months (est. @ 15%)" />
        <div className="flex items-end gap-3 mt-3 h-24">
          {(data.commission_trend || []).map((m, i) => {
            const max = Math.max(...(data.commission_trend || []).map(x => x.amount), 1);
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1" data-testid={`commission-bar-${m.month}`}>
                <span className="text-[9px] font-mono" style={{ color: C.slate }}>{fmt$(m.amount)}</span>
                <div className="w-full rounded-t-sm transition-all" style={{ height: `${Math.max((m.amount / max) * 70, 2)}px`, background: C.green + 'CC' }} />
                <span className="text-[10px] font-medium" style={{ color: C.slate }}>{m.month}</span>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="broker-recent-activity">
        <SectionHead title="Recent Activity" />
        {data.recent_activity.length === 0 ? <Empty msg="No recent activity." /> : (
          <div className="space-y-0 mt-2">
            {data.recent_activity.map((a, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b text-xs" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: a.type === 'policy' ? C.green : C.amber }} />
                <p className="flex-1 truncate" style={{ color: C.navy }}>{a.label}</p>
                {a.amount != null && <span className="font-mono shrink-0" style={{ color: C.navy }}>{fmt$(a.amount)}</span>}
                <span className="text-[10px] shrink-0" style={{ color: C.slate }}>{a.date ? new Date(a.date).toLocaleDateString('en-AU') : ''}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <NotificationPrefsCard />
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <Card className="px-3 py-3 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid={testid}>
      <div className="flex items-center gap-1.5">
        <Icon className="w-3 h-3" style={{ color }} />
        <p className="text-[9px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{label}</p>
      </div>
      <p className="text-lg font-semibold mt-1 font-mono" style={{ color }}>{value}</p>
      {sub && <p className="text-[10px] font-mono" style={{ color: C.slate }}>{sub}</p>}
    </Card>
  );
}

function SectionHead({ title, action, onAction }) {
  return (
    <div className="flex items-center justify-between">
      <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.slate }}>{title}</h3>
      {action && (
        <button onClick={onAction} className="flex items-center gap-1 text-[10px] font-medium hover:underline" style={{ color: C.blue }}>
          {action}<ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function Empty({ msg }) {
  return <p className="text-xs py-6 text-center" style={{ color: C.slate }}>{msg}</p>;
}
